import axios from 'axios';
import { getApiUrl } from './apiConfig';
import { getStoredLanguage } from '@shared/i18n';

const API_BASE_URL = getApiUrl();

export const SYSTEM_TOKEN = import.meta.env.VITE_SYSTEM_TOKEN;

const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 30000;

export const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

export const STORAGE_KEYS = {
  VIEWER_TOKEN: 'viewer_token',
  MEMBER_TOKEN: 'member_token',
  USER_INFO: 'user'
};

// --- LocalStorage 工具函數 ---

export const getStorage = (key) => localStorage.getItem(key);

export const setStorage = (key, value) => localStorage.setItem(key, value);

export const removeStorage = (key) => localStorage.removeItem(key);

// --- 保留 cookie 工具（供外部使用，內部不再依賴） ---

const IS_PROD = import.meta.env.PROD;
const COOKIE_OPTIONS = `path=/;${IS_PROD ? ' Secure;' : ''} SameSite=Lax`;

export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const setCookie = (name, value) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${COOKIE_OPTIONS}`;
};

export const removeCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${COOKIE_OPTIONS}`;
};

/**
 * 取得當前 Token
 * 優先級：Member Token > Viewer Token > System Token
 */
export const getCurrentToken = () => {
  return getStorage(STORAGE_KEYS.MEMBER_TOKEN)
    || getStorage(STORAGE_KEYS.VIEWER_TOKEN)
    || SYSTEM_TOKEN;
};

/**
 * Axios 實例配置
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(API_TIMEOUT),
  headers: {
    'Content-Type': 'application/json',
    ...(SYSTEM_TOKEN && { 'System-Token': SYSTEM_TOKEN }),
  },
});

// 後端 language key，之後要改只需要改這裡
const LANG_KEY = 'lang';

apiClient.interceptors.request.use(
  (config) => {
    const existing = config.headers['X-Auth-Token'] || config.headers['x-auth-token'];
    if (!existing) {
      const token = getCurrentToken();
      if (token) {
        config.headers['X-Auth-Token'] = token;
      }
    }

    if (!config.skipLang) {
      const lang = getStoredLanguage();
      if (lang && lang !== 'zh-TW') {
        config.params = { ...config.params, [LANG_KEY]: lang };
      }
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data?.result_code !== undefined && data.result_code !== 200) {
      const message = data.reason || data.message;
      console.error('❌ API Business Error:', data.result_code, message);
      return Promise.reject({
        status: data.result_code,
        message,
        data,
      });
    }
    return data;
  },
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.reason || error.response?.data?.message || error.message;

    console.error('❌ API Error:', status, message);

    if (error.response) {
      switch (status) {
        case 401:
          if (getStorage(STORAGE_KEYS.MEMBER_TOKEN)) {
            // 已登入但 token 失效 → 強制登出並跳登入頁
            console.error('Member token 已失效，強制登出');
            removeStorage(STORAGE_KEYS.MEMBER_TOKEN);
            removeStorage(STORAGE_KEYS.USER_INFO);
            window.dispatchEvent(new Event('auth-change'));
            window.location.href = '/login';
          } else {
            // 未登入但 viewer token 失效 → 重新取得後 reload
            console.warn('Viewer token 已失效，重新取得');
            removeStorage(STORAGE_KEYS.VIEWER_TOKEN);
            apiClient.get('/v1/auth/viewerToken/get', {
              headers: { 'X-Auth-Token': SYSTEM_TOKEN }
            }).then((res) => {
              if (res.token) {
                setStorage(STORAGE_KEYS.VIEWER_TOKEN, res.token);
                window.location.reload();
              }
            }).catch((e) => {
              console.error('重新取得 Viewer Token 失敗:', e);
            });
          }
          break;
        case 403:
          console.error('沒有權限訪問此資源');
          break;
        case 404:
          console.error('請求的資源不存在');
          break;
        case 422:
          console.error('資料驗證失敗:', error.response.data);
          break;
        case 500:
          console.error('伺服器錯誤，請稍後再試');
          break;
        case 502:
        case 503:
          console.error('服務暫時不可用，請稍後再試');
          break;
        default:
          console.error('發生錯誤:', message);
      }
    } else if (error.request) {
      console.error('網路錯誤，請檢查網路連線');
    } else {
      console.error('請求配置錯誤:', error.message);
    }

    return Promise.reject({
      status,
      message,
      data: error.response?.data,
      error,
    });
  }
);

export const setViewerToken = (token) => {
  setStorage(STORAGE_KEYS.VIEWER_TOKEN, token);
};

export const setMemberToken = (token) => {
  setStorage(STORAGE_KEYS.MEMBER_TOKEN, token);
};

export const setUserInfo = (userInfo) => {
  setStorage(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
};

export const getUserInfo = () => {
  const raw = getStorage(STORAGE_KEYS.USER_INFO);
  return raw ? JSON.parse(raw) : null;
};

export const clearAllTokens = () => {
  removeStorage(STORAGE_KEYS.VIEWER_TOKEN);
  removeStorage(STORAGE_KEYS.MEMBER_TOKEN);
  removeStorage(STORAGE_KEYS.USER_INFO);
};

export default apiClient;
export { API_BASE_URL };
